import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { getMondayOfCurrentWeek } from "../utils/utils";
import {
  getAvailableSlots,
  setEditingSlotR,
  setSelectedSlotR,
} from "../store/slices/rendezVousSlice";

const Calendar = ({
  horaires,
  reduxPrestations,
  ets,
  rdvEtablissement = null,
  setCalendarTouched = null,
  dispatchAvailabilities,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState();
  const [visibleDays, setVisibleDays] = useState(7); // Start with 7 days (desktop)
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const dispatch = useDispatch();

  const oneEtablissement = useSelector(
    (state) => state.etablissements.selectedEtablissement
  );
  let selectedEtablissement = null;
  const { employe } = useSelector((state) => state.rendezvous);
  selectedEtablissement = ets === "store" ? oneEtablissement : rdvEtablissement;
  const { availableSlots, slotsStatus, selectedSlotRdv } = useSelector(
    (state) => state.rendezvous
  );

  // Check screen size and adjust visible days
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleDays(2); // Show 2 days on mobile
      } else {
        setVisibleDays(7); // Show all days on desktop
      }
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle touch events for swipe navigation
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      // Swipe left - go to next days
      if (currentDayIndex + visibleDays < 7) {
        setCurrentDayIndex((prev) =>
          Math.min(prev + visibleDays, 7 - visibleDays)
        );
      }
    } else if (diff < -50) {
      // Swipe right - go to previous days
      if (currentDayIndex > 0) {
        setCurrentDayIndex((prev) => Math.max(prev - visibleDays, 0));
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Generate time slots (unchanged from your original code)
  const generateTimeSlots = (startHour, endHour) => {
    let slots = [];
    let currentTime = startHour;
    while (currentTime < endHour) {
      const hourString =
        currentTime < 10 ? `0${currentTime}` : `${currentTime}`;
      slots.push(`${hourString}:00`, `${hourString}:30`);
      currentTime++;
    }
    return slots;
  };

  // Get week dates (unchanged from your original code)
  const getWeekDates = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay() + 1);
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDates.push(day);
    }
    return weekDates;
  };

  // In your Calendar component, modify the navigation functions:
  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7); // Avance de 7 jours
    const nextMonday = getMondayOfCurrentWeek(newDate); // Puis prend le lundi de cette semaine
    setCurrentDate(nextMonday);
    // newDate.setDate(currentDate.getDate() + 6);
    // setCurrentDate(newDate);
    setCurrentDayIndex(0);
    dispatchAvailabilities(
      employe?.checked ? employe?.selectedEmploye : null,
      newDate
    ); // Pass the new date
  };

  const prevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7); // Recule de 7 jours
    const prevMonday = getMondayOfCurrentWeek(newDate); // Puis prend le lundi de cette semaine
    setCurrentDate(prevMonday);
    setCurrentDayIndex(0);
    dispatchAvailabilities(
      employe?.checked ? employe?.selectedEmploye : null,
      newDate
    ); // Pass the new date
  };
  // Navigation entre jours (mobile) ou semaines (desktop)
  const handleNext = () => {
    if (visibleDays < 7) {
      // Mode mobile
      if (currentDayIndex + visibleDays < 7) {
        setCurrentDayIndex((prev) =>
          Math.min(prev + visibleDays, 7 - visibleDays)
        );
      } else {
        nextWeek(); // Si on arrive à la fin, passer à la semaine suivante
      }
    } else {
      // Mode desktop
      nextWeek();
    }
  };

  const handlePrev = () => {
    if (visibleDays < 7) {
      // Mode mobile
      if (currentDayIndex > 0) {
        setCurrentDayIndex((prev) => Math.max(prev - visibleDays, 0));
      } else {
        prevWeek(); // Si on est au début, revenir à la semaine précédente
      }
    } else {
      // Mode desktop
      prevWeek();
    }
  };
  // Get the current week's dates
  const weekDates = getWeekDates(currentDate);
  
  const timeSlots = generateTimeSlots(6, 21);

  // Get only the visible days
  const visibleWeekDates = weekDates.slice(
    currentDayIndex,
    currentDayIndex + visibleDays
  );
  const isSlotAvailable = (date, time, pauses = []) => {
    // 1. Get the day of the week
    const dayOfWeek = date
      .toLocaleString("fr-FR", { weekday: "long" })
      .toLowerCase();
    // const capitalizedDay = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
    // console.log(`Checking day: ${capitalizedDay}`);

    // 2. Find the schedule
    const daySchedule = horaires?.find(
      (schedule) =>
        schedule.jour.toLowerCase() === dayOfWeek?.toLowerCase() &&
        schedule.etablissement_id === selectedEtablissement?.id
    );

    if (!daySchedule || daySchedule.fermee === 1) {
      // console.log(`No schedule or closed: day=${capitalizedDay}, etablissement_id=${selectedEtablissement?.id}`);
      return false;
    }

    // 3. Extract opening and closing times
    const [openHour, openMinute] = daySchedule.horaire_ouverture
      .split(":")
      .map(Number);
    const [closeHour, closeMinute] = daySchedule.horaire_fermeture
      .split(":")
      .map(Number);
    const openingTimeInMinutes = openHour * 60 + openMinute;
    const closingTimeInMinutes = closeHour * 60 + closeMinute;
    // console.log(
    //   `Open: ${openingTimeInMinutes}, Close: ${closingTimeInMinutes}`
    // );

    // 4. Handle breaks (from pauses array or daySchedule)
    let pauseStartInMinutes = null;
    let pauseEndInMinutes = null;

    // Check for pauses in the provided pauses array
    const dateString = date.toISOString().split("T")[0];
    const relevantPause = pauses.find(
      (pause) =>
        pause.start_time.startsWith(dateString) && pause.reason === "pause"
    );

    if (relevantPause) {
      const [startHour, startMinute] = relevantPause.start_time
        .split(" ")[1]
        .split(":")
        .map(Number);
      const [endHour, endMinute] = relevantPause.end_time
        .split(" ")[1]
        .split(":")
        .map(Number);
      pauseStartInMinutes = startHour * 60 + startMinute;
      pauseEndInMinutes = endHour * 60 + endMinute;
      // console.log(`Pause: ${pauseStartInMinutes}–${pauseEndInMinutes}`);
    } else if (
      daySchedule.continuous === 0 &&
      daySchedule.pause_debut &&
      daySchedule.pause_fin
    ) {
      // Fallback to existing pause_debut/pause_fin logic
      const [pauseStartHour, pauseStartMinute] = daySchedule.pause_debut
        .split(":")
        .map(Number);
      const [pauseEndHour, pauseEndMinute] = daySchedule.pause_fin
        .split(":")
        .map(Number);
      pauseStartInMinutes = pauseStartHour * 60 + pauseStartMinute;
      pauseEndInMinutes = pauseEndHour * 60 + pauseEndMinute;
      // console.log(`Schedule Pause: ${pauseStartInMinutes}–${pauseEndInMinutes}`);
    }

    // 5. Calculate total duration
    const totalDuration = reduxPrestations.reduce((total, prestation) => {
      return total + prestation.duree;
    }, 0);
    // console.log(`Total duration: ${totalDuration} minutes (${totalDuration / 30} slots)`);

    // 6. Convert slot time to minutes
    const [startHour, startMinute] = time.split(":").map(Number);
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = startTimeInMinutes + totalDuration;
    // console.log(`Slot: ${startTimeInMinutes}–${endTimeInMinutes}`);

    // 7. Check if slot is within opening hours
    if (
      startTimeInMinutes < openingTimeInMinutes ||
      endTimeInMinutes > closingTimeInMinutes
    ) {
      // console.log(`Slot out of bounds: start=${startTimeInMinutes}, end=${endTimeInMinutes}`);
      return false;
    }

    // 8. Check if slot overlaps with break
    if (pauseStartInMinutes && pauseEndInMinutes) {
      if (
        startTimeInMinutes < pauseEndInMinutes &&
        endTimeInMinutes > pauseStartInMinutes
      ) {
        //  console.log(`Slot overlaps break: ${startTimeInMinutes}–${endTimeInMinutes} vs ${pauseStartInMinutes}–${pauseEndInMinutes}`);
        return false;
      }
    }

    // 9. Check each 30-minute slot
    const slotsNeeded = Math.ceil(totalDuration / 30);
    for (let i = 0; i < slotsNeeded; i++) {
      const slotTimeInMinutes = startTimeInMinutes + i * 30;
      const slotHour = Math.floor(slotTimeInMinutes / 60);
      const slotMinute = slotTimeInMinutes % 60;
      const slotTime = `${slotHour.toString().padStart(2, "0")}:${slotMinute
        .toString()
        .padStart(2, "0")}`;
      if (!isSingleSlotAvailable(date, slotTime)) {
        // console.log(`Slot unavailable: ${dateString} ${slotTime}`);
        return false;
      }
    }

    return true;
  };
  // Fonction helper pour vérifier la disponibilité d'un seul créneau de 30 minutes
  const isSingleSlotAvailable = (date, time) => {
    if (!availableSlots || !availableSlots.slots) return false;

    const now = new Date();
    const dateString = date.toISOString().split("T")[0];
    const slotDateTime = new Date(`${dateString} ${time}`);

    // 1. Vérifier si le créneau est dans le passé
    if (slotDateTime < now) {
      return false;
    }

    // 2. Vérifier si la date est marquée comme fermée
    if (availableSlots.slots[dateString]?.includes("closed")) {
      return false;
    }

    // 3. Vérifier les créneaux réservés
    const dateSlots = availableSlots.slots[dateString];
    //console.log(dateSlots);
    if (!dateSlots) {
      return true; // Pas de réservation pour cette date
    }

    // Cette fonction vérifie si un créneau horaire est disponible en comparant avec les créneaux déjà réservés
    return !dateSlots.some((slot) => {
      // Si le créneau est marqué comme "fermé", on le considère comme indisponible
      if (slot?.reason === "closed") return true;

      // Déclaration des variables pour les heures de début et fin
      let startTime, endTime;

      // Si le créneau est sous forme de string (ex: "09:00-10:00")
      if (typeof slot === "string") {
        // On sépare la string pour obtenir l'heure de début et de fin
        [startTime, endTime] = slot.split("-");
      }
      // Si le créneau est un objet avec des propriétés start_time et end_time
      else if (slot.start_time && slot.end_time) {
        // On extrait juste l'heure (HH:MM) des timestamps complets
        startTime = slot.start_time.split(" ")[1];
        endTime = slot.end_time.split(" ")[1];
      }
      // Format de créneau non reconnu
      else {
        return false;
      }

      const slotStart = new Date(`${dateString} ${startTime}`); // Début du créneau réservé
      const slotEnd = new Date(`${dateString} ${endTime}`); // Fin du créneau réservé
      const currentSlot = new Date(`${dateString} ${time}`); // Créneau qu'on veut vérifier
      const nextSlot = new Date(currentSlot.getTime() + 30 * 60000); // 30 minutes après le créneau à vérifier

      // Debug: affiche les dates pour vérification
      // console.log(currentSlot, slotEnd, nextSlot, slotStart);

      // Vérifie s'il y a un chevauchement entre:
      // - Le créneau qu'on veut vérifier (currentSlot à nextSlot)
      // - Un créneau déjà réservé (slotStart à slotEnd)
      // Retourne true si les créneaux se chevauchent (donc indisponible)
      return currentSlot < slotEnd && nextSlot > slotStart;
    });
  };
  // Skeleton loader (unchanged from your original code)
  const SkeletonSlot = () => (
    <div className="h-10 bg-gray-200 rounded mb-2 animate-pulse" />
  );

  if (slotsStatus === "loading")
    return (
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
        {Array.from({ length: visibleDays }).map((_, dayIndex) => (
          <div key={dayIndex} className="flex flex-col space-y-2">
            {Array.from({ length: 2 }).map((_, slotIndex) => (
              <SkeletonSlot key={slotIndex} />
            ))}
          </div>
        ))}
      </div>
    );

  return (
    <div className="calendar-container">
      {/* Calendar Header with Week Navigation */}
      <div className="calendar-header">
        {/* <button className="nav-button" onClick={prevWeek}>
          <img src="/images/prev.svg" alt="prev" />
        </button> */}
        <button
          className="nav-button"
          onClick={handlePrev}
          // disabled={visibleDays < 7 && currentDayIndex === 0}
        >
          <img src="/images/prev.svg" alt="prev" />
        </button>
        {/* Weekdays container - now shows only visible days */}
        <div
          className="weekdays"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {visibleWeekDates.map((date, index) => (
            <div key={index} className="day-header">
              <span className="day-name">
                {date.toLocaleString("fr-FR", { weekday: "short" })}
              </span>
              <span className="day-date">
                {date.toLocaleString("fr-FR", {
                  day: "numeric",
                  month: "short",
                })}
              </span>
            </div>
          ))}
        </div>

        {/* <button className="nav-button" onClick={nextWeek}>
          <img src="/images/next.svg" alt="next" />
        </button> */}
        <button
          className="nav-button"
          onClick={handleNext}
          // disabled={visibleDays < 7 && currentDayIndex + visibleDays >= 7}
        >
          <img src="/images/next.svg" alt="next" />
        </button>
      </div>

      {/* Calendar Body with Days and Time Slots */}
      <div
        className="calendar-body"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {visibleWeekDates.map((date, dateIndex) => (
          <div key={dateIndex} className="day-slots">
            {timeSlots.map((slot, slotIndex) => {
              const isAvailable = isSlotAvailable(date, slot);
              const isSelected = selectedSlot === `${dateIndex}-${slotIndex}`;
              const isCurrentReservation =
                selectedSlotRdv?.slot &&
                selectedSlotRdv.slot.date ===
                  date.toISOString().split("T")[0] &&
                selectedSlotRdv.slot.time === slot;

              return (
                <div
                  key={slotIndex}
                  className={`slot 
                    ${isSelected ? "selected" : ""} 
                    ${isCurrentReservation ? "current-reservation" : ""}
                    ${!isAvailable ? "disabled" : ""}`}
                  onClick={() => {
                    if (isAvailable) {
                      const slotData = {
                        date: date.toISOString().split("T")[0],
                        time: slot,
                        fullDate: new Date(
                          `${date.toISOString().split("T")[0]} ${slot}`
                        ),
                      };
                      setSelectedSlot(`${dateIndex}-${slotIndex}`);
                      dispatch(setSelectedSlotR(slotData));
                      dispatch(setEditingSlotR(false));
                      if (setCalendarTouched) {
                        setCalendarTouched(true);
                      }
                    }
                  }}
                >
                  <span className="slot-time">{slot}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Mobile navigation dots */}
      {/* {visibleDays < 7 && (
        <div className="flex justify-center mt-4 space-x-2">
          {[0, 2, 4].map((index) => (
            <button
              key={index}
              className={`link-blue mr-8 ${currentDayIndex === index ? 'bg-blue-500' : 'bg-gray-300'}`}
              onClick={() => setCurrentDayIndex(index)}
              aria-label={`Show days ${index + 1}-${index + 2}`}
            ><h1>.</h1></button>
          ))}
        </div>
      )} */}
    </div>
  );
};

export default Calendar;
